// The trends endpoints accept at most 10 ids per request.
export const TRENDS_MAX_IDS_PER_REQUEST = 10

// How many chunked trends POSTs one query keeps in flight. A cold catalogue load
// splits ~150 families into ~16 chunks; sending them all at once saturates the
// browser's per-host connection pool and hits the backend in one burst.
export const TRENDS_MAX_CONCURRENT_REQUESTS = 4

export function chunkIds(ids: string[], size: number): string[][] {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size))
  return chunks
}

export type ConcurrencyLimiter = <T>(task: () => Promise<T>) => Promise<T>

// Runs scheduled tasks at most `limit` at a time, in scheduling order. Each task gets
// its own promise, so a caller can hand one out before the task has started.
export function createConcurrencyLimiter(limit: number): ConcurrencyLimiter {
  let active = 0
  const queue: (() => void)[] = []

  const startNext = () => {
    if (active >= limit) return
    const start = queue.shift()
    if (start) start()
  }

  return <T>(task: () => Promise<T>) =>
    new Promise<T>((resolve, reject) => {
      queue.push(() => {
        active += 1
        const settle = () => {
          active -= 1
          startNext()
        }
        task().then(
          (value) => {
            settle()
            resolve(value)
          },
          (error) => {
            settle()
            reject(error)
          }
        )
      })
      startNext()
    })
}

// `Promise.all(items.map(fn))` with at most `limit` calls of `fn` pending at once.
export function mapWithConcurrency<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
  const schedule = createConcurrencyLimiter(limit)
  return Promise.all(items.map((item) => schedule(() => fn(item))))
}
